import React, { useState } from "react";
import { observer } from "mobx-react";
import accountListPickerState from "./state";
import "./accountListPicker.scss";

/**
 * "Save to account list" popup (list-builder companies tab). Pick an
 * existing account list or create one inline, then save the selected
 * companies into it. Account lists hold companies, not contacts.
 */
const AccountListPickerPopup = observer(() => {
  const [selectedId, setSelectedId] = useState("");
  const [newListName, setNewListName] = useState("");
  const [creating, setCreating] = useState(false);

  if (!accountListPickerState.showPopup) return null;

  const { lists, listsLoading, saving, companies } = accountListPickerState;

  const handleSave = async () => {
    if (saving || creating) return;
    if (newListName.trim()) {
      setCreating(true);
      const created = await accountListPickerState.createList(newListName.trim());
      setCreating(false);
      if (created) {
        await accountListPickerState.saveToList(created);
        setNewListName("");
        setSelectedId("");
      }
      return;
    }
    const list = lists.find(item => item.id === selectedId);
    if (list) {
      await accountListPickerState.saveToList(list);
      setSelectedId("");
    }
  };

  const canSave = Boolean(newListName.trim() || selectedId) && !saving && !creating;

  return (
    <div className="accountListPickerOverlay">
      <div className="accountListPicker">
        <div className="accountListPicker__header">
          <h4>Save to account list</h4>
          <span
            className="material-icons-outlined accountListPicker__close"
            onClick={accountListPickerState.close}
          >
            close
          </span>
        </div>
        <div className="accountListPicker__subtitle">
          {companies.length} compan{companies.length === 1 ? "y" : "ies"} selected
        </div>
        <div className="accountListPicker__body">
          <div className="accountListPicker__newList">
            <i className="material-icons-outlined">add</i>
            <input
              type="text"
              placeholder="Create a new list…"
              value={newListName}
              maxLength={80}
              onChange={e => {
                setNewListName(e.target.value);
                if (e.target.value.trim()) setSelectedId("");
              }}
            />
          </div>
          <div className="accountListPicker__divider">or choose existing</div>
          <div className="accountListPicker__lists">
            {listsLoading ? (
              Array.from({ length: 3 }, (value, index) => (
                <div
                  className="accountListPicker__skeleton linear-background"
                  key={`alp_skel_${index}`}
                />
              ))
            ) : lists.length ? (
              lists.map(list => (
                <label
                  key={list.id}
                  className={`accountListPicker__item ${
                    selectedId === list.id ? "selected" : ""
                  }`}
                >
                  <input
                    type="radio"
                    name="accountListPick"
                    checked={selectedId === list.id}
                    onChange={() => {
                      setSelectedId(list.id);
                      setNewListName("");
                    }}
                  />
                  <span className="accountListPicker__itemName">{list.name}</span>
                  <span className="accountListPicker__itemCount">
                    {list.count} account{list.count === 1 ? "" : "s"}
                  </span>
                </label>
              ))
            ) : (
              <div className="accountListPicker__empty">
                No account lists yet. Create your first one above.
              </div>
            )}
          </div>
        </div>
        <div className="accountListPicker__footer">
          <button
            type="button"
            className="accountListPicker__btn"
            onClick={accountListPickerState.close}
          >
            Cancel
          </button>
          <button
            type="button"
            className="accountListPicker__btn primary"
            disabled={!canSave}
            onClick={handleSave}
          >
            {saving || creating ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
});

export default AccountListPickerPopup;
